import React from "react";

import Text from "@src/lib/ui/Text";
import AddMediaDialog from "@src/features/app/dialogs/addMedia/AddMediaDialog";
import { MediaFragment } from "@src/fragments.generated";
import { Button } from "@src/lib/ui/Button";
import PhotoOutlineIcon from "@src/lib/icons/18px/photo-outline";
import FileDropZone from "@src/lib/ui/FileDropZone";
import { gql } from "@apollo/client";
import { useToast } from "@src/lib/hooks/useToast";
import { emptyTranslationSet } from "@src/features/knowledgeCheck/utils/prompts";
import { useMessageBlastDialogCreateMediaMutation } from "@src/features/messaging/MessageBlastFileDropZone.generated";
import { AddMediaResources_TrainingResourceFragment } from "@src/features/app/dialogs/addMedia/Resources.generated";
import { SentMessage_MessageBlastMediaFragment } from "./SentMessage.generated";
import { Ff } from "@src/types.generated";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";

type Props = {
  messageBlastMedias: Set<SentMessage_MessageBlastMediaFragment>;
  setMessageBlastMedias: (
    newSet: Set<SentMessage_MessageBlastMediaFragment>,
  ) => void;
};

const nextIndex = (
  medias: Set<SentMessage_MessageBlastMediaFragment>,
): number => {
  const indices = Array.from(medias, (m) => m.index);
  return indices.length === 0 ? 0 : Math.max(...indices) + 1;
};

const MessageBlastFileDropZone: React.FC<Props> = (props) => {
  const { addErrorToast } = useToast();
  const libraryItemAttachmentsEnabled = useFeatureFlag(
    Ff.BlastLibraryItemAttachments,
  );

  const [isFileUploading, setIsFileUploading] = React.useState(false);
  const [createMedia] = useMessageBlastDialogCreateMediaMutation({
    onCompleted(data) {
      if (data.createMedia.success && data.createMedia.media) {
        const media = data.createMedia.media;
        const messageBlastMediaObj = {
          id: media.id,
          media,
          pinned: false,
          index: nextIndex(props.messageBlastMedias),
        };
        props.setMessageBlastMedias(
          new Set([...props.messageBlastMedias, messageBlastMediaObj]),
        );
      } else {
        addErrorToast({
          ...data.createMedia.error,
          callsite: "add_media_dialog",
        });
      }
    },
    onError: (error) => {
      addErrorToast({
        ...error,
        callsite: "add_media_dialog",
      });
    },
  });
  const handleOnCreateMedia = React.useCallback(
    (url: string) => {
      createMedia({
        variables: {
          input: {
            mediaUrls: emptyTranslationSet({
              en: url,
              translationsDisabled: true,
            }),
          },
        },
      });
    },
    [createMedia],
  );
  return (
    <FileDropZone
      disabled={false}
      setMediaUrl={handleOnCreateMedia}
      isFileUploading={isFileUploading}
      setIsFileUploading={setIsFileUploading}
    >
      <div className="mb-4 flex flex-1 flex-col items-center justify-center self-stretch rounded-xl border-2 border-dotted bg-gray-1 py-4">
        <Text
          variant="compact-title"
          className="mb-2 items-center text-muted-foreground"
        >
          Drop files here or{" "}
          <span className="cursor-pointer text-foreground underline">
            upload file
          </span>
        </Text>
        <AddMediaDialog
          title="Add media"
          onMediaChange={(media?: MediaFragment) => {
            if (media) {
              const messageBlastMediaObj = {
                id: media.id,
                media,
                pinned: false,
                index: nextIndex(props.messageBlastMedias),
              };
              props.setMessageBlastMedias(
                new Set([...props.messageBlastMedias, messageBlastMediaObj]),
              );
            }
          }}
          onResourceChange={(
            trainingResource?: AddMediaResources_TrainingResourceFragment,
          ) => {
            const newMedia = trainingResource?.publishedVersion?.media;

            if (
              libraryItemAttachmentsEnabled &&
              trainingResource?.libraryItem &&
              trainingResource?.publishedVersion
            ) {
              const newMessageBlastMedia = {
                id: Date.now(),
                pinned: false,
                index: nextIndex(props.messageBlastMedias),
                libraryItem: {
                  id: trainingResource?.libraryItem?.id,
                  type: trainingResource?.libraryItem?.type,
                  name: trainingResource?.libraryItem?.name,
                  trainingResource: {
                    id: trainingResource?.id,
                    publishedVersion: {
                      id: trainingResource?.publishedVersion?.id,
                      type: trainingResource?.publishedVersion?.type,
                      media: trainingResource?.publishedVersion?.media,
                      richTextDocument:
                        trainingResource?.publishedVersion?.richTextDocument,
                    },
                  },
                },
              };
              props.setMessageBlastMedias(
                new Set([...props.messageBlastMedias, newMessageBlastMedia]),
              );
            } else if (newMedia) {
              const newMessageBlastMedia = {
                id: newMedia.id,
                media: newMedia,
                pinned: false,
                index: nextIndex(props.messageBlastMedias),
              };
              props.setMessageBlastMedias(
                new Set([...props.messageBlastMedias, newMessageBlastMedia]),
              );
            }
          }}
          trigger={
            <Button
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <PhotoOutlineIcon />
              Add media
            </Button>
          }
        />
      </div>
    </FileDropZone>
  );
};

gql`
  mutation MessageBlastDialogCreateMedia($input: MediaInput!) {
    createMedia(input: $input) {
      success
      error {
        code
      }
      media {
        ...Media
      }
    }
  }
`;

export default MessageBlastFileDropZone;
